import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useClientAuth } from "../../context/ClientAuth";

const LINKS = [
  { to: "/join", label: "Early access", testid: "nav-link-early-access" },
  { to: "/for-salons", label: "For Salons", testid: "nav-link-salons" },
  { to: "/join", label: "Waitlist", testid: "nav-link-waitlist" },
  { to: "/login", label: "Sign in", testid: "nav-link-signin" },
];

export default function Nav({ overHero = false }) {
  const { client } = useClientAuth();
  const links = LINKS.map((l) => (l.testid === "nav-link-signin" && client ? { to: "/my-bookings", label: "My bookings", testid: "nav-link-my-bookings" } : l));
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Light (bone) chrome unless we're floating over the dark hero and haven't scrolled.
  const dark = overHero && !scrolled && !open;
  const linkBase = dark ? "text-bone/75 hover:text-bone" : "text-ink/65 hover:text-ink";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        dark
          ? "bg-transparent"
          : scrolled
          ? "bg-bone/95 backdrop-blur-md border-b border-sand/60"
          : "bg-bone/70 backdrop-blur-sm"
      }`}
      data-testid="site-nav"
    >
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 h-14 md:h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="nav-home-link" onClick={() => setOpen(false)}>
          <img
            src={dark ? "/brand/fyllr-wordmark-cream.png" : "/brand/fyllr-wordmark-oxblood.png"}
            alt="FYLLR"
            className="h-5 w-auto"
            data-testid="nav-wordmark-image"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden sm:flex items-center gap-1 sm:gap-3 md:gap-5" aria-label="Primary" data-testid="nav-desktop-links">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`text-[13px] transition-colors px-2 ${linkBase}`}
              data-testid={l.testid}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        {/* Mobile controls */}
        <div className="flex sm:hidden items-center gap-2">
          <Link
            to="/join"
            className="bg-burgundy px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-bone transition-colors hover:bg-espresso"
            data-testid="nav-early-access-mobile"
          >
            Early access
          </Link>
          <button
            onClick={() => setOpen((o) => !o)}
            className={`p-1.5 ${dark ? "text-bone" : "text-ink"}`}
            aria-label="Menu"
            data-testid="nav-menu-toggle"
          >
            {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {open && (
        <div className="sm:hidden bg-bone/98 backdrop-blur-md border-t border-sand/60 px-5 py-4" data-testid="nav-mobile-menu">
          <div className="flex flex-col">
            <Link
              to="/join"
              onClick={() => setOpen(false)}
              className="py-3 text-[15px] text-ink/80 border-b border-sand/50"
              data-testid="nav-early-access-menu"
            >
              Get early access
            </Link>
            {links.filter((l) => l.testid !== "nav-link-early-access").map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="py-3 text-[15px] text-ink/80 border-b border-sand/50 last:border-0"
                data-testid={`${l.testid}-mobile`}
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
